import React from 'react';
import PropTypes from 'prop-types';
import * as UploadUtils from '../../../../utils/upload-utils';
import { FilePickerState } from '../file-picker/FilePickerState';
import FilePicker from '../file-picker/FilePicker';

class S3FilePicker extends React.Component {
    static preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    constructor(props) {
        super(props);
        const { editfile, defaultpath } = this.props;

        this.state = {
            error: undefined, // for error message
            eventState: editfile ? FilePickerState.uploaded : FilePickerState.start,
            // 'start', 'drag', 'drop', 'uploading', 'uploaded', 'failed'
            hasFile: editfile, // is a file curently uploaded in the filepicker
            fileName: '', // name of uploaded file
            previewUrl: editfile ? defaultpath : '',
            logoUrlUnchanged: true, // set logoURL name as 'logoUnchanged' unless new logo uploaded
            imageHash: '',
        };
        this.validate = UploadUtils.validate.bind(this);
        this.handleFileChanged = this.handleFileChanged.bind(this);
        this.handleRemoveFile = this.handleRemoveFile.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.upload = this.upload.bind(this);
        this.uploadToS3 = UploadUtils.uploadToS3.bind(this);
    }

    componentDidMount() {
        // reset imageHash for each render, ensure background image is the latest
        this.setState(() => ({
            imageHash: Date.now(),
        }));
    }

    /// Process a new file upload event
    async handleFileChanged(file) {
        const {
            passerror, onhandlefilechanged, identifyImageType,
        } = this.props;
        let { error } = this.state;
        let nextState = FilePickerState.uploading;

        if (!file) {
            nextState = FilePickerState.failed; // sets nextState to 'failed'
            error = 'The file you have you selected is invalid.';
            this.setState(() => ({
                eventState: nextState,
                hasFile: false,
                error,
            }));
            passerror(error);
        } else {
            const fileName = file.name;
            const previewUrl = window.URL.createObjectURL(file);
            if (onhandlefilechanged !== undefined
                && identifyImageType !== undefined && identifyImageType(file)) {
                const isSquareImage = await onhandlefilechanged(previewUrl, file);
                if (isSquareImage) {
                    this.setState({
                        eventState: nextState,
                        hasFile: false,
                        fileName,
                        previewUrl,
                    }, () => {
                        this.upload(file);
                    });
                } else {
                    this.setState(() => ({
                        eventState: FilePickerState.failed,
                        hasFile: false,
                        error: 'The file you have you selected is invalid.',
                    }));

                    passerror('Image uploaded should be square');
                }
            } else {
                this.setState({
                    eventState: nextState,
                    hasFile: false,
                    fileName,
                    previewUrl,
                }, () => {
                    this.upload(file);
                });
            }
        }
    }

    handleRemoveFile() {
        const {
            bucketpath, xamzcredential, xamzdate, policy, xamzsignature,
        } = this.props;

        this.setState({
            fileName: '',
            previewUrl: '',
            logoUrlUnchanged: false,
        }, () => {
            if (process.env.NODE_ENV === 'test') {
                this.handleLoad();
            } else {
                const file = null;
                this.uploadToS3(
                    file,
                    bucketpath,
                    xamzcredential,
                    xamzdate,
                    policy,
                    xamzsignature,
                    this.handleLoad,
                );
            }
        });
    }

    handleLoad() {
        const { passerror } = this.props;
        const { fileName } = this.state;

        // validate the file upload in the parent form.
        passerror('');

        if (fileName) {
            // if fileName is empty and not in test environment, it means removed file
            this.setState(() => ({
                hasFile: true,
                eventState: FilePickerState.uploaded,
            }));
        } else {
            // once remove file, set eventState='start' so filePicker show initial state
            this.setState(() => ({
                hasFile: true,
                eventState: FilePickerState.start,
            }));
        }
    }

    /// Starts moving the file to S3 temp bucket in the background
    /// When the form is submitted the server can get the file from 'props.bucketpath'
    upload(file) {
        const {
            maxsize, extensions, passerror, bucketpath, xamzcredential,
            xamzdate, policy, xamzsignature,
        } = this.props;
        const message = this.validate(file, maxsize, ...extensions);

        if (message) {
            passerror(message);
            this.setState(() => ({
                hasFile: false,
                error: message,
                eventState: FilePickerState.failed,
            }));
        } else if (process.env.NODE_ENV === 'test') {
            this.handleLoad();
        } else {
            this.uploadToS3(
                file,
                bucketpath,
                xamzcredential,
                xamzdate,
                policy,
                xamzsignature,
                this.handleLoad,
            );
            this.setState({ logoUrlUnchanged: false });
        }
    }

    render() {
        const {
            hasFile, logoUrlUnchanged, eventState, previewUrl,
            fileName, imageHash,
        } = this.state;
        const {
            name, optional, maxsize, round, preview, bucketpath, passerror, editfile, square,
        } = this.props;

        let backgroundimage = `url(${`${previewUrl}`})`;
        let filePickerInputValue = '';

        if (hasFile) {
            // if save the SVG as a base64 url

            // if file has been successfully loaded - set filename and value of hidden input
            filePickerInputValue = bucketpath;
            if (logoUrlUnchanged) {
                backgroundimage = `url(${`${previewUrl}`}?${`${imageHash}`})`;
            }
        }
        const filePickerDefaultSubtitle = `Max upload size: ${maxsize}MB`;

        return (
            <FilePicker
                handleFileChanged={this.handleFileChanged}
                name={name}
                optional={optional}
                hasFile={hasFile}
                logoUrlUnchanged={logoUrlUnchanged}
                eventState={eventState}
                maxsize={maxsize}
                round={round}
                square={square}
                preview={preview}
                fileName={fileName}
                handleRemoveFile={this.handleRemoveFile}
                filePickerInputValue={filePickerInputValue}
                backgroundimage={backgroundimage}
                passerror={passerror}
                editfile={editfile}
                filePickerDefaultSubtitle={filePickerDefaultSubtitle}
            />
        );
    }
}

S3FilePicker.propTypes = {
    name: PropTypes.string.isRequired,
    maxsize: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    extensions: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    bucketpath: PropTypes.string.isRequired,
    xamzcredential: PropTypes.string.isRequired,
    xamzdate: PropTypes.string.isRequired,
    policy: PropTypes.string.isRequired,
    xamzsignature: PropTypes.string.isRequired,
    passerror: PropTypes.func,
    round: PropTypes.bool,
    square: PropTypes.bool,
    preview: PropTypes.bool,
    editfile: PropTypes.bool,
    defaultpath: PropTypes.string,
    optional: PropTypes.bool,
    onhandlefilechanged: PropTypes.func,
    identifyImageType: PropTypes.func,
};

S3FilePicker.defaultProps = {
    passerror: () => { },
    maxsize: undefined,
    extensions: undefined,
    round: false,
    preview: false,
    editfile: false,
    defaultpath: null,
    optional: false,
    square: false,
    onhandlefilechanged: () => { },
    identifyImageType: () => { },
};

export default S3FilePicker;
